import { computed, Signal, signal, WritableSignal } from '@angular/core';
import { CraftingRequirements, Item } from '../items.model';
import { Location } from '../location.model';
import { PriceType } from '../market.model';
import { FAME_BONUS_PREMIUM, PRICE_TYPES, SALES_TAXES, SETUP_FEES } from '../../constants/market.constant';
import { SERVERS } from '../../constants/servers.constant';
import { JsonReaderService } from 'src/app/services/json-reader/json-reader.service';
import { MarketHistoriesResponse, MarketResponse } from '../ao-data-interfaces';
import { AoDataGetterService } from 'src/app/services/ao-data-getter/ao-data-getter.service';
import { LOCATIONS } from '../../constants/locations.constant';
import { Achievement } from '../achievement.model';
import { findMatchingAchievements, getFocusEfficiency } from '../../functions/achievements-functions';
import { firstValueFrom, forkJoin, take } from 'rxjs';
import { SpecLevel } from './specs-models';
import { sign } from 'node:crypto';

// MARK: --DoneCraft
export class DoneCraft {
  id?: string;
  craftGroup: CraftGroup;
  totalFocus: number;
  totalCraftFamePlayerTotal: number;
  totalFee: number;
  totalExpenses: number;
  totalIncome: number;
  totalResult: number;
  craftDate?: Date;
  totalInputWeight: number;
  totalOutputWeight: number;

  constructor(
    id: string | undefined,
    craftGroup: CraftGroup,
    totalFocus: number,
    totalCraftFamePlayerTotal: number,
    totalCraftFee: number,
    totalExpenses: number,
    totalIncome: number,
    totalResult: number,
    totalInputWeight: number,
    totalOutputWeight: number,
    craftDate?: Date
  ) {
    this.id = id;
    this.craftGroup = craftGroup;
    this.totalFocus = totalFocus;
    this.totalCraftFamePlayerTotal = totalCraftFamePlayerTotal;
    this.totalFee = totalCraftFee;
    this.totalExpenses = totalExpenses;
    this.totalIncome = totalIncome;
    this.totalResult = totalResult;
    this.craftDate = craftDate;
    this.totalInputWeight = totalInputWeight;
    this.totalOutputWeight = totalOutputWeight;
  }
}

// MARK: --CraftGroup
export class CraftGroup {
  public id = signal<string | undefined>(undefined);
  public crafts = signal<Craft[]>([]);
  public name: WritableSignal<string> = signal('New Craft Group');
  public server: WritableSignal<string> = signal(SERVERS[0].id);
  public hasPremium: WritableSignal<boolean> = signal(true);
  public hideUnselectedCrafts: WritableSignal<boolean> = signal(false);
  public hideConsumablesPicker: WritableSignal<boolean> = signal(true);
  public hideFameInfo: WritableSignal<boolean> = signal(true);
  public hideFocusInfo: WritableSignal<boolean> = signal(false);
  public hideMissingPrices: WritableSignal<boolean> = signal(false);
  public onlyMissingPricesCrafts: WritableSignal<boolean> = signal(false);
  public showCharts: WritableSignal<boolean> = signal(false);
  public averageNumberOfDays: WritableSignal<number> = signal(14);
  public onlyMissingPricesItems: WritableSignal<boolean> = signal(false);
  public hideUnusedItems: WritableSignal<boolean> = signal(false);
  public onlyShowMainRecipes: WritableSignal<boolean> = signal(false);
  public updatedAt: WritableSignal<Date | undefined> = signal(new Date());

  private readonly inputItemInstances = new Map<string, InputItem>();
  private readonly outputItemInstances = new Map<string, OutputItem>();

  public isOkToSaveAsDoneCraft = computed(() => {
    return this.selectedCraftsCount() > 0 && !this.selectedCrafts().some((craft) => craft.hasMissingPrices());
  });
  public inputItems: Signal<InputItem[]> = computed(() => {
    const allInputItems = new Set<InputItem>();
    this.crafts().forEach((craft) => {
      craft.craftInputs().forEach((craftInput) => {
        allInputItems.add(craftInput.inputItem);
      });
    });
    const sortedItems = Array.from(allInputItems);
    sortedItems.sort((a, b) => this._getItemSortKey(a).localeCompare(this._getItemSortKey(b)));
    return sortedItems;
  });
  public inputItemsWeight = computed(() => {
    return this.inputItems().reduce((acc, inputItem) => acc + inputItem.totalWeight(), 0);
  });

  public outputItems: Signal<OutputItem[]> = computed(() => {
    const allOutputItems = new Set<OutputItem>();
    this.crafts().forEach((craft) => {
      craft.craftOutputs.forEach((craftOutput) => {
        allOutputItems.add(craftOutput.outputItem);
      });
    });
    const sortedItems = Array.from(allOutputItems);
    sortedItems.sort((a, b) => this._getItemSortKey(a).localeCompare(this._getItemSortKey(b)));
    return sortedItems;
  });

  public usedProductOutputItems: Signal<OutputItem[]> = computed(() => {
    return this.outputItems().filter((outputItem) => outputItem.type === 'Product' && outputItem.totalAmount() > 0);
  });

  public outputItemsWeight = computed(() => {
    return this.outputItems().reduce((acc, outputItem) => acc + outputItem.totalWeight(), 0);
  });

  public marketHistoriesResponses: WritableSignal<MarketHistoriesResponse[]> = signal([]);
  public marketResponses: WritableSignal<MarketResponse[]> = signal([]);

  public craftsCount = computed(() => {
    return this.crafts().length;
  });

  public selectedCrafts = computed(() => {
    return this.crafts().filter((craft) => craft.selected());
  });

  public selectedCraftsCount = computed(() => {
    return this.selectedCrafts().length;
  });

  public missingPricesCount = computed(() => {
    return this.crafts().filter((craft) => craft.hasMissingPrices()).length;
  });

  public craftCategoryTopItems = computed(() => {
    const allProducts = this.crafts().map((craft) => craft.product);
    const groupedItems = new Map<string, Item>();

    for (const product of allProducts) {
      const groupName = product.groupUniqueName;
      const productTier = product.tier ?? 0;
      const productEnchantment = product.enchantmentLevel ?? 0;

      if (!groupedItems.has(groupName)) {
        groupedItems.set(groupName, product);
      } else {
        const currentTopItem = groupedItems.get(groupName)!;
        const currentTopItemTier = currentTopItem.tier ?? 0;
        const currentTopItemEnchantment = currentTopItem.enchantmentLevel ?? Infinity;

        if (productTier > currentTopItemTier) {
          groupedItems.set(groupName, product);
        } else if (productTier === currentTopItemTier) {
          if (productEnchantment < currentTopItemEnchantment) {
            groupedItems.set(groupName, product);
          }
        }
      }
    }
    return Array.from(groupedItems.values());
  });

  public allCraftAchievementsUnique = computed(() => {
    const all = this.crafts().flatMap((craft) => craft.craftAchievements());
    const uniqueMap = new Map<string, Achievement>();
    all.forEach((achievement) => {
      if (!uniqueMap.has(achievement.id)) {
        uniqueMap.set(achievement.id, achievement);
      }
      achievement.playerLevel = this.userSpecsSignal().find((spec) => spec.spec === achievement.id)?.level ?? 0;
    });
    return Array.from(uniqueMap.values());
  });

  public inputOutputItemsUniqueNames = computed(() => {
    const allNames = [...this.inputItems().map((x) => x.item.uniqueName), ...this.outputItems().map((x) => x.item.uniqueName)];
    return Array.from(new Set(allNames));
  });

  private _getItemSortKey(itemContainer: { item: Item }): string {
    const item = itemContainer.item;
    return `${item.groupUniqueName.toLowerCase()}_${item.tier}_${item.enchantmentLevel}`;
  }

  public getOrCreateInputItem(item: Item, type: CraftInputType): InputItem {
    if (!this.inputItemInstances.has(item.uniqueName)) {
      this.inputItemInstances.set(item.uniqueName, new InputItem(this, item, type));
    }
    return this.inputItemInstances.get(item.uniqueName)!;
  }

  public getOrCreateOutputItem(item: Item, type: CraftOutputType): OutputItem {
    if (!this.outputItemInstances.has(item.uniqueName)) {
      this.outputItemInstances.set(item.uniqueName, new OutputItem(this, item, type));
    }
    return this.outputItemInstances.get(item.uniqueName)!;
  }

  public deleteCraftCategory(category: string): void {
    this.crafts.update((currentCrafts) => currentCrafts.filter((craft) => craft.product.groupUniqueName !== category));
    // InputItems and OutputItems will recompute automatically.
    // The effects in the constructor will handle cleanup of unused instances from maps.
  }

  public addCraft(product: Item): Craft[] | undefined {
    // Check if the product is already in the crafts list
    const existingCrafts = this.crafts().filter((craft) => craft.product === product);
    if (existingCrafts.length > 0) {
      return existingCrafts;
    }

    // Create the craft(s)
    const addedCrafts: Craft[] = [];
    product.craftingRequirements.forEach((req, index) => {
      // The Craft constructor will now use getOrCreateInputItem/OutputItem
      // to get or create the necessary item instances.
      const newCraft = new Craft(product, req, index === 0, this);
      this.crafts.update((currentCrafts) => [...currentCrafts, newCraft]);
      addedCrafts.push(newCraft);
    });

    return addedCrafts.length > 0 ? addedCrafts : undefined;
  }

  public removeCraft(craftToRemove: Craft): void {
    this.crafts.update((currentCrafts) => currentCrafts.filter((c) => c !== craftToRemove));
    // InputItems and OutputItems will recompute automatically.
    // The effects in the constructor will handle cleanup of unused instances from maps.
  }

  public async refreshPrices(): Promise<void> {
    if (this.inputOutputItemsUniqueNames().length === 0) {
      return;
    }

    const prices$ = this.aoDataGetterService.getPrices(this.inputOutputItemsUniqueNames(), undefined, undefined, this.server()).pipe(take(1));
    const history$ = this.aoDataGetterService.getHistory(this.inputOutputItemsUniqueNames(), undefined, undefined, undefined, undefined, undefined, this.server()).pipe(take(1));

    try {
      const [pricesResponse, historyResponse] = await firstValueFrom(forkJoin([prices$, history$]));

      this.marketResponses.set(pricesResponse);
      this.marketHistoriesResponses.set(historyResponse);
      this.setAllInputPricesToAodp(true);
      this.setAllOutputPricesToAodp(true);
    } catch (error) {
      console.error('Error refreshing prices in CraftGroup:', error);
    }
  }

  public setAllInputPricesToAodp(onlyIfZero: boolean = false): void {
    this.inputItems().forEach((inputItem) => {
      if (!onlyIfZero || !inputItem.buyUserPrice()) {
        inputItem.buyUserPrice.set(inputItem.aodpPrice() ?? 0);
      }
      if (!onlyIfZero || !inputItem.sellUserPrice()) {
        inputItem.sellUserPrice.set(inputItem.leftOverAodpPrice() ?? 0);
      }
    });
  }
  public setAllOutputPricesToAodp(onlyIfZero: boolean = false): void {
    this.outputItems().forEach((outputItem) => {
      if (!onlyIfZero || !outputItem.sellUserPrice()) {
        outputItem.sellUserPrice.set(outputItem.aodpPrice() ?? 0);
      }
    });
  }
  public totalFocus = computed(() => {
    return this.selectedCrafts().reduce((acc, craft) => acc + craft.totalFocus(), 0);
  });
  public totalCraftFamePlayerTotal = computed(() => {
    return this.selectedCrafts().reduce((acc, craft) => acc + craft.craftFamePlayerTotal(), 0);
  });
  public totalCraftFee = computed(() => {
    return this.selectedCrafts().reduce((acc, craft) => acc + craft.totalCraftFee(), 0);
  });
  public totalExpenses = computed(() => {
    return this.selectedCrafts().reduce((acc, craft) => acc + craft.totalExpenses(), 0);
  });
  public totalIncome = computed(() => {
    return this.selectedCrafts().reduce((acc, craft) => acc + craft.totalIncome(), 0);
  });
  public totalResult = computed(() => {
    return this.selectedCrafts().reduce((acc, craft) => acc + craft.result(), 0);
  });
  public totalInputWeight = computed(() => {
    return this.selectedCrafts().reduce((acc, craft) => acc + craft.totalInputsWeight(), 0);
  });
  public totalOutputWeight = computed(() => {
    return this.selectedCrafts().reduce((acc, craft) => acc + craft.totalOutputsWeight(), 0);
  });
  // MARK: constructor
  constructor(
    public jsonReaderService: JsonReaderService,
    public aoDataGetterService: AoDataGetterService,
    public achievementsSignal: Signal<Achievement[]>,
    public userSpecsSignal: Signal<SpecLevel[]>
  ) {
    // effect calls for cleaning up inputItemInstances and outputItemInstances have been removed.
    // This means these maps will only grow and not be cleared of unused items,
    // potentially leading to increased memory usage over time.
  }
}

// MARK: --Craft
export class Craft {
  public craftGroup: CraftGroup;
  public product: Item;
  public recipe: CraftingRequirements;
  public craftFameJournalPerRun: number;
  public isMainRecipe: boolean;
  public craftInputs: WritableSignal<CraftInput[]> = signal([]);
  public craftOutputs: CraftOutput[] = [];
  public numberOfRuns: WritableSignal<number> = signal(10);
  public rrr: WritableSignal<number> = signal(0.153);
  public useFocus: WritableSignal<boolean> = signal(false);
  public craftLocation: WritableSignal<Location | undefined> = signal(undefined);
  public craftFeePerNutrition: WritableSignal<number> = signal(300);
  public selected: WritableSignal<boolean> = signal(false);
  public extraExpenses: WritableSignal<number> = signal(0);
  public extraIncome: WritableSignal<number> = signal(0);
  public ignoreJournals: WritableSignal<boolean> = signal(false);
  public consumables = computed(() => {
    return this.craftInputs().filter((input) => input.inputType === 'Consumable');
  });
  public craftAchievements = computed(() => {
    return findMatchingAchievements(this.craftGroup.achievementsSignal(), this.product, 'craftitemfame');
  });
  public focusTrigger = signal(false);
  public focusEfficiency = computed(() => {
    const trigger = this.focusTrigger(); // Trigger the effect to recalculate when focusTrigger changes - CHEAT
    return getFocusEfficiency(this.craftAchievements(), this.product, 'craftingfocuscostreduction');
  });
  public totalFocus = computed(() => {
    if (this.useFocus()) {
      return this.craftGroup.jsonReaderService.getFocusToCraft(this.recipe, this.focusEfficiency()) * (this.recipe.amountCrafted ?? 1) * this.numberOfRuns();
    }
    return 0;
  });
  public resultPerFocus = computed(() => {
    if (this.totalFocus() === 0) {
      return 0;
    }
    return this.result() / this.totalFocus();
  });
  public craftFameJournalTotal = computed(() => {
    return this.craftFameJournalPerRun * this.numberOfRuns();
  });
  public craftFamePlayerTotal = computed(() => {
    var fame = this.craftFameJournalTotal() * (this.product.craftFameFactor ?? 1);
    if (this.craftGroup.hasPremium()) {
      fame *= 1 + FAME_BONUS_PREMIUM;
    }
    return fame;
  });
  public craftFamePlayerPerExpenses = computed(() => {
    if (this.totalExpenses() === 0) {
      return 0;
    }
    return this.craftFamePlayerTotal() / this.totalExpenses();
  });
  public craftFamePlayerPerFocus = computed(() => {
    if (this.totalFocus() === 0) {
      return 0;
    }
    return this.craftFamePlayerTotal() / this.totalFocus();
  });
  public missingPricesItemsNames = computed(() => {
    const missingInputs = this.craftInputs()
      .filter((input) => input.inputItem.hasMissingPrices())
      .map((input) => input.inputItem.item.localizedName);
    const missingOutputs = this.craftOutputs.filter((output) => output.outputItem.hasMissingPrices()).map((output) => output.outputItem.item.localizedName);
    return [...missingInputs, ...missingOutputs];
  });
  public hasMissingPrices = computed(() => {
    return this.missingPricesItemsNames().length > 0;
  });
  public totalCraftFee = computed(() => {
    return this.craftGroup.jsonReaderService.getCraftingFee(this.product, this.craftFeePerNutrition()) * this.numberOfRuns() * (this.recipe.amountCrafted ?? 1);
  });
  public totalFixedCost = computed(() => {
    return (this.recipe.silver ?? 0) * this.numberOfRuns();
  });
  public totalExpenses = computed(() => {
    const inputCosts = this.craftInputs().reduce((acc, input) => {
      return acc + input.buyCostWithFee() + input.sellTaxesCost() + input.sellSetupFeeCost();
    }, 0);
    const outputCosts = this.craftOutputs.reduce((acc, output) => {
      return acc + output.setupFeeCost() + output.salesTaxCost();
    }, 0);
    return inputCosts + outputCosts + this.totalCraftFee() + this.totalFixedCost() + this.extraExpenses();
  });
  public totalIncome = computed(() => {
    const inputIncome = this.craftInputs().reduce((acc, input) => {
      return acc + input.sellIncomeWithoutFeeOrTax();
    }, 0);
    const outputIncome = this.craftOutputs.reduce((acc, output) => {
      return acc + output.sellIncomeWithoutFeeOrTax();
    }, 0);
    return inputIncome + outputIncome + this.extraIncome();
  });
  public result = computed(() => {
    return this.totalIncome() - this.totalExpenses();
  });
  public resultPercent = computed(() => {
    if (this.totalExpenses() === 0) {
      return 0;
    }
    return this.result() / this.totalExpenses();
  });

  public totalInputsWeight = computed(() => {
    return this.craftInputs().reduce((acc, input) => acc + input.totalWeight(), 0);
  });
  public totalOutputsWeight = computed(() => {
    return this.craftOutputs.reduce((acc, output) => acc + output.totalWeight(), 0);
  });

  constructor(product: Item, recipe: CraftingRequirements, isMainRecipe: boolean, craftGroup: CraftGroup) {
    this.craftGroup = craftGroup;
    this.product = product;
    this.recipe = recipe;
    this.isMainRecipe = isMainRecipe;
    this.craftFameJournalPerRun = this.craftGroup.jsonReaderService.getItemCraftFame(this.product, this.recipe) ?? 0;

    this.recipe.craftResources.map((input) => {
      if (input.ingredientItem) {
        const inputItem = this.craftGroup.getOrCreateInputItem(input.ingredientItem, 'Ingredient');
        const newInput = new CraftInput(this, inputItem, 'Ingredient', input.count, input.maxReturnAmount);
        this.craftInputs.update((currentInputs) => [...currentInputs, newInput]);
      }
    });

    const outputItem = this.craftGroup.getOrCreateOutputItem(product, 'Product');
    const newOutput = new CraftOutput(this, outputItem, 'Product', this.recipe.amountCrafted ?? 1);
    this.craftOutputs.push(newOutput);

    const journals = this.craftGroup.jsonReaderService.getJournalByItem(product);
    if (journals.empty && journals.full) {
      const inputEmptyItem = this.craftGroup.getOrCreateInputItem(journals.empty, 'Journal');
      const journalsPerRun = this.craftFameJournalPerRun / (inputEmptyItem?.item.journalMaxFame ?? 1);
      const newInput = new CraftInput(this, inputEmptyItem, 'Journal', journalsPerRun, 0);
      this.craftInputs.update((currentInputs) => [...currentInputs, newInput]);

      const outputFullItem = this.craftGroup.getOrCreateOutputItem(journals.full, 'Journal');
      const newJournalOutput = new CraftOutput(this, outputFullItem, 'Journal', journalsPerRun);
      this.craftOutputs.push(newJournalOutput);
    }
  }

  public addConsumable(consumable: Item, amount?: number): void {
    // Check if the consumable is already added
    const existingInput = this.craftInputs().find((input) => input.inputItem.item === consumable);
    if (existingInput) {
      return;
    }

    const inputItem = this.craftGroup.getOrCreateInputItem(consumable, 'Consumable');
    const newCraftInput = new CraftInput(this, inputItem, 'Consumable', amount ?? 1, 0);
    this.craftInputs.update((currentInputs) => [...currentInputs, newCraftInput]);
  }

  public removeConsumable(consumable: Item): void {
    this.craftInputs.update((currentInputs) => currentInputs.filter((input) => input.inputItem.item !== consumable));
    // The cleanup of the InputItem instance from CraftGroup's map
    // will be handled by the effect in CraftGroup if the item is no longer used anywhere.
  }
}

// MARK: --InputItem
export class InputItem {
  public craftGroup: CraftGroup;
  public type: CraftInputType;
  public item: Item;
  public buyUserPrice = signal(0);
  public sellUserPrice = signal(0);
  public aodpPrices: Signal<{
    minSellPrice: MarketResponse | undefined;
    maxBuyPrice: MarketResponse | undefined;
  }> = computed(() => {
    return this.craftGroup.aoDataGetterService.getMinAndMaxPrice(this.item, this.buyLocation() ?? LOCATIONS.Caerleon, this.craftGroup.marketResponses(), undefined);
  });
  public aodpPrice = computed(() => {
    if (this.buyPriceType() === PRICE_TYPES.sell) {
      return this.aodpPrices().minSellPrice?.sellPriceMin;
    }
    if (this.buyPriceType() === PRICE_TYPES.buy) {
      return this.aodpPrices().maxBuyPrice?.buyPriceMax;
    }
    if (this.buyPriceType() === PRICE_TYPES.average) {
      return this.aodpAverage().averagePrice;
    }
    return 0;
  });
  public aodpPriceAge = computed(() => {
    if (this.buyPriceType() === PRICE_TYPES.sell && this.aodpPrice()) {
      return (new Date().getTime() - (this.aodpPrices().minSellPrice?.sellPriceMinDate?.getTime() ?? 0)) / 1000 / 60 / 60;
    }
    if (this.buyPriceType() === PRICE_TYPES.buy && this.aodpPrice()) {
      return (new Date().getTime() - (this.aodpPrices().maxBuyPrice?.buyPriceMaxDate?.getTime() ?? 0)) / 1000 / 60 / 60;
    }
    return undefined;
  });
  public aodpAverage: Signal<{ averagePrice: number | undefined; averageAmount: number | undefined }> = computed(() => {
    return this.craftGroup.aoDataGetterService.getAveragePriceAndAmount(this.item, this.buyLocation() ?? LOCATIONS.Caerleon, this.craftGroup.marketHistoriesResponses(), undefined, this.craftGroup.averageNumberOfDays());
  });
  public buyLocation: WritableSignal<Location | undefined> = signal(LOCATIONS.Caerleon);
  public buyPriceType: WritableSignal<PriceType | undefined> = signal(PRICE_TYPES.sell);
  public leftOverSellPriceType: WritableSignal<PriceType | undefined> = signal(PRICE_TYPES.sell);
  public leftOverAodpPrice = computed(() => {
    if (this.leftOverSellPriceType() === PRICE_TYPES.sell) {
      return this.aodpPrices().minSellPrice?.sellPriceMin;
    }
    if (this.leftOverSellPriceType() === PRICE_TYPES.buy) {
      return this.aodpPrices().maxBuyPrice?.buyPriceMax;
    }
    if (this.leftOverSellPriceType() === PRICE_TYPES.average) {
      return this.aodpAverage().averagePrice;
    }
    return 0;
  });
  public totalAmount = computed(() => {
    return this.craftGroup.selectedCrafts().reduce((acc, craft) => {
      const input = craft.craftInputs().find((input) => input.inputItem.item === this.item);
      if (input) {
        return acc + input.amount().amountUsed;
      }
      return acc;
    }, 0);
  });
  public totalWeight = computed(() => {
    return (this.item.weight ?? 0) * this.totalAmount();
  });

  public totalLeftOvers = computed(() => {
    return this.craftGroup.selectedCrafts().reduce((acc, craft) => {
      const input = craft.craftInputs().find((input) => input.inputItem.item === this.item);
      if (input) {
        return acc + input.amount().leftOverAmount;
      }
      return acc;
    }, 0);
  });

  public hasMissingPrices = computed(() => {
    return this.buyUserPrice() === 0 || this.sellUserPrice() === 0;
  });

  // Aggregetad values
  public totalBuyCostWithoutFee = computed(() => {
    return this.craftGroup.selectedCrafts().reduce((acc, craft) => {
      const input = craft.craftInputs().find((input) => input.inputItem.item === this.item);
      if (input) {
        return acc + input.buyCostWithoutFee();
      }
      return acc;
    }, 0);
  });
  public totalBuySetupFeeCost = computed(() => {
    return this.craftGroup.selectedCrafts().reduce((acc, craft) => {
      const input = craft.craftInputs().find((input) => input.inputItem.item === this.item);
      if (input) {
        return acc + input.buySetupFeeCost();
      }
      return acc;
    }, 0);
  });
  public totalBuyCostWithFee = computed(() => {
    return this.totalBuyCostWithoutFee() + this.totalBuySetupFeeCost();
  });
  public totalSellIncomeWithoutFeeOrTax = computed(() => {
    return this.craftGroup.selectedCrafts().reduce((acc, craft) => {
      const input = craft.craftInputs().find((input) => input.inputItem.item === this.item);
      if (input) {
        return acc + input.sellIncomeWithoutFeeOrTax();
      }
      return acc;
    }, 0);
  });
  public totalSellSetupFeeCost = computed(() => {
    return this.craftGroup.selectedCrafts().reduce((acc, craft) => {
      const input = craft.craftInputs().find((input) => input.inputItem.item === this.item);
      if (input) {
        return acc + input.sellSetupFeeCost();
      }
      return acc;
    }, 0);
  });
  public totalSellTaxesCost = computed(() => {
    return this.craftGroup.selectedCrafts().reduce((acc, craft) => {
      const input = craft.craftInputs().find((input) => input.inputItem.item === this.item);
      if (input) {
        return acc + input.sellTaxesCost();
      }
      return acc;
    }, 0);
  });
  public totalSellIncomeWithFeeAndTax = computed(() => {
    return this.totalSellIncomeWithoutFeeOrTax() - this.totalSellSetupFeeCost() - this.totalSellTaxesCost();
  });

  constructor(craftGroup: CraftGroup, item: Item, type: CraftInputType) {
    this.craftGroup = craftGroup;
    this.item = item;
    this.type = type;
  }
}

// MARK: --OutputItem
export class OutputItem {
  public craftGroup: CraftGroup;
  public type: CraftOutputType;
  public item: Item;
  public sellUserPrice = signal(0);
  public sellLocation: WritableSignal<Location | undefined>;
  public sellPriceType: WritableSignal<PriceType | undefined>;
  public aodpPrices: Signal<{
    minSellPrice: MarketResponse | undefined;
    maxBuyPrice: MarketResponse | undefined;
  }> = computed(() => {
    return this.craftGroup.aoDataGetterService.getMinAndMaxPrice(this.item, this.sellLocation() ?? LOCATIONS.Caerleon, this.craftGroup.marketResponses(), undefined);
  });
  public aodpPrice = computed(() => {
    if (this.sellPriceType() === PRICE_TYPES.sell) {
      return this.aodpPrices().minSellPrice?.sellPriceMin;
    }
    if (this.sellPriceType() === PRICE_TYPES.buy) {
      return this.aodpPrices().maxBuyPrice?.buyPriceMax;
    }
    if (this.sellPriceType() === PRICE_TYPES.average) {
      return this.aodpAverage().averagePrice;
    }
    return 0;
  });
  public aodpPriceAge = computed(() => {
    if (this.sellPriceType() === PRICE_TYPES.sell && this.aodpPrice()) {
      return (new Date().getTime() - (this.aodpPrices().minSellPrice?.sellPriceMinDate?.getTime() ?? 0)) / 1000 / 60 / 60;
    }
    if (this.sellPriceType() === PRICE_TYPES.buy && this.aodpPrice()) {
      return (new Date().getTime() - (this.aodpPrices().maxBuyPrice?.buyPriceMaxDate?.getTime() ?? 0)) / 1000 / 60 / 60;
    }
    return undefined;
  });
  public aodpAverage: Signal<{ averagePrice: number | undefined; averageAmount: number | undefined }> = computed(() => {
    return this.craftGroup.aoDataGetterService.getAveragePriceAndAmount(this.item, this.sellLocation() ?? LOCATIONS.Caerleon, this.craftGroup.marketHistoriesResponses(), undefined, this.craftGroup.averageNumberOfDays());
  });
  public totalAmount = computed(() => {
    return this.craftGroup.selectedCrafts().reduce((acc, craft) => {
      const output = craft.craftOutputs.find((output) => output.outputItem.item === this.item);
      if (output) {
        return acc + output.amount();
      }
      return acc;
    }, 0);
  });
  public totalWeight = computed(() => {
    return (this.item.weight ?? 0) * this.totalAmount();
  });
  public hasMissingPrices = computed(() => {
    return this.sellUserPrice() === 0;
  });

  // Aggregetad values
  public totalSellIncomeWithoutFeeOrTax = computed(() => {
    return this.craftGroup.selectedCrafts().reduce((acc, craft) => {
      const output = craft.craftOutputs.find((output) => output.outputItem.item === this.item);
      if (output) {
        return acc + output.sellIncomeWithoutFeeOrTax();
      }
      return acc;
    }, 0);
  });
  public totalSetupFeeCost = computed(() => {
    return this.craftGroup.selectedCrafts().reduce((acc, craft) => {
      const output = craft.craftOutputs.find((output) => output.outputItem.item === this.item);
      if (output) {
        return acc + output.setupFeeCost();
      }
      return acc;
    }, 0);
  });
  public totalSalesTaxCost = computed(() => {
    return this.craftGroup.selectedCrafts().reduce((acc, craft) => {
      const output = craft.craftOutputs.find((output) => output.outputItem.item === this.item);
      if (output) {
        return acc + output.salesTaxCost();
      }
      return acc;
    }, 0);
  });
  public totalSellIncomeWithFeeAndTax = computed(() => {
    return this.totalSellIncomeWithoutFeeOrTax() - this.totalSetupFeeCost() - this.totalSalesTaxCost();
  });

  constructor(craftGroup: CraftGroup, item: Item, type: CraftOutputType) {
    this.craftGroup = craftGroup;
    this.item = item;
    this.type = type;

    if (type === 'Journal') {
      this.sellLocation = signal(LOCATIONS.Caerleon);
      this.sellPriceType = signal(PRICE_TYPES.sell);
    } else {
      this.sellLocation = signal(LOCATIONS.BlackMarket);
      this.sellPriceType = signal(PRICE_TYPES.average);
    }
  }
}

// MARK: --CraftInput
export class CraftInput {
  public craft: Craft;
  public inputItem: InputItem;
  public inputType: CraftInputType;
  public inputAmountPerRun: WritableSignal<number> = signal(0);
  public maxReturnAmount: number | undefined;

  public amount: Signal<InputAmount> = computed(() => {
    if (this.inputType === 'Ingredient') {
      return this.craft.craftGroup.jsonReaderService.getTotalIngredientAmountAfterRRR(this.craft.numberOfRuns(), this.inputAmountPerRun(), this.maxReturnAmount, this.craft.rrr());
    }
    if (this.inputType === 'Journal') {
      if (this.craft.ignoreJournals()) {
        return { amountUsed: 0, leftOverAmount: 0 };
      }
      return { amountUsed: this.inputAmountPerRun() * this.craft.numberOfRuns(), leftOverAmount: 0 };
    }
    if (this.inputType === 'Consumable') {
      return { amountUsed: this.inputAmountPerRun(), leftOverAmount: 0 };
    }
    return { amountUsed: 0, leftOverAmount: 0 };
  });

  public totalWeight = computed(() => {
    return (this.inputItem.item.weight ?? 0) * this.amount().amountUsed;
  });

  // MARK: Buying
  public buyCostWithoutFee = computed(() => {
    return this.inputItem.buyUserPrice() * this.amount().amountUsed;
  });

  public buySetupFee = computed(() => {
    if (this.inputItem.buyPriceType() === PRICE_TYPES.buy) {
      return SETUP_FEES.normal;
    }
    return SETUP_FEES.zero;
  });

  public buySetupFeeCost = computed(() => {
    return this.buySetupFee().value * this.buyCostWithoutFee();
  });

  public buyCostWithFee = computed(() => {
    return this.buyCostWithoutFee() + this.buySetupFeeCost();
  });

  // MARK: Left Over Selling

  public sellIncomeWithoutFeeOrTax = computed(() => {
    return this.inputItem.sellUserPrice() * this.amount().leftOverAmount;
  });

  public sellSetupFee = computed(() => {
    if (this.inputItem.leftOverSellPriceType() === PRICE_TYPES.sell) {
      return SETUP_FEES.normal;
    }
    return SETUP_FEES.zero;
  });

  public sellSetupFeeCost = computed(() => {
    return this.sellSetupFee().value * this.sellIncomeWithoutFeeOrTax();
  });

  public sellTaxes = computed(() => {
    if (this.craft.craftGroup.hasPremium()) {
      return SALES_TAXES.premium;
    }
    return SALES_TAXES.normal;
  });

  public sellTaxesCost = computed(() => {
    return this.sellTaxes().value * this.sellIncomeWithoutFeeOrTax();
  });

  public sellIncomeWithFeeAndTax = computed(() => {
    return this.sellIncomeWithoutFeeOrTax() - this.sellSetupFeeCost() - this.sellTaxesCost();
  });

  // MARK: constructor
  constructor(craft: Craft, inputItem: InputItem, craftInputType: CraftInputType, inputAmountPerRun: number, maxReturnAmount: number | undefined) {
    this.craft = craft;
    this.inputItem = inputItem;
    this.inputType = craftInputType;
    this.inputAmountPerRun.set(inputAmountPerRun);
    this.maxReturnAmount = maxReturnAmount;
  }
}

// MARK: --CraftOutput
export class CraftOutput {
  public craft: Craft;
  public outputItem: OutputItem;
  public outputType: CraftOutputType;
  public outputAmountPerRun: number;

  public amount = computed(() => {
    if (this.outputType === 'Journal') {
      if (this.craft.ignoreJournals()) {
        return 0;
      }
    }
    return this.craft.numberOfRuns() * this.outputAmountPerRun;
  });

  public totalWeight = computed(() => {
    return (this.outputItem.item.weight ?? 0) * this.amount();
  });

  // MARK: Selling

  public sellIncomeWithoutFeeOrTax = computed(() => {
    return this.outputItem.sellUserPrice() * this.amount();
  });

  public setupFee = computed(() => {
    if (this.outputItem.sellPriceType() === PRICE_TYPES.sell) {
      return SETUP_FEES.normal;
    }
    return SETUP_FEES.zero;
  });

  public setupFeeCost = computed(() => {
    return this.setupFee().value * this.sellIncomeWithoutFeeOrTax();
  });

  public salesTax = computed(() => {
    if (this.craft.craftGroup.hasPremium()) {
      return SALES_TAXES.premium;
    }
    return SALES_TAXES.normal;
  });

  public salesTaxCost = computed(() => {
    return this.salesTax().value * this.sellIncomeWithoutFeeOrTax();
  });

  public sellIncomeWithFeeAndTax = computed(() => {
    return this.sellIncomeWithoutFeeOrTax() - this.setupFeeCost() - this.salesTaxCost();
  });

  // MARK: constructor
  constructor(craft: Craft, outputItem: OutputItem, craftOutputType: CraftOutputType, outputAmountPerRun: number) {
    this.craft = craft;
    this.outputItem = outputItem;
    this.outputType = craftOutputType;
    this.outputAmountPerRun = outputAmountPerRun;
  }
}

// MARK: InputAmount
export interface InputAmount {
  amountUsed: number;
  leftOverAmount: number;
}

// MARK: InputType
export type CraftInputType = 'Ingredient' | 'Journal' | 'Consumable';
export type CraftOutputType = 'Product' | 'Journal';
